/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { isApolloError } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-use";
import { useEstateGroupedExportMutation } from "~/generated/graphql";
import { SUCCESS_TEXT } from "~/constants";
import { showSimpleInfo, showSuccess } from "~/services/toast";
import { handleError, toInputError } from "~/tools";

type Filters = {
  chartJobId: string;
  startDate: string;
  endDate: string;
};

export function useGroupingExport() {
  const [estateGroupedExport, mutationState] = useEstateGroupedExportMutation();
  const { user } = useAuth0();
  const { href } = useLocation();

  const exportGroups = async ({ chartJobId, startDate, endDate }: Filters) => {
    try {
      await estateGroupedExport({
        variables: {
          input: {
            chartJobId,
            startDate,
            endDate,
            viewUrl: href?.replace(/page=\d+&/, "page=0&") ?? "",
            notificationEmail: user?.email ?? "",
          },
        },
      });
      showSuccess(SUCCESS_TEXT.export, false);
    } catch (err) {
      const error = toInputError(err);
      const isExportInProgress =
        isApolloError(error) &&
        error.graphQLErrors.some(
          (graphQlError) =>
            graphQlError.extensions.code === "EXPORT_IN_PROGRESS"
        );

      if (isExportInProgress) {
        showSimpleInfo(
          "Similar export action recently requested. Please retry later.",
          false
        );
      } else {
        handleError(error);
      }
    }
  };

  return [exportGroups, mutationState] as const;
}
